'use client'

import { useState, useEffect } from 'react'
import { useLocale } from '@/lib/i18n'
import { initializeGTM, setGTMConsent, shouldShowConsentBanner, isMainApplication, hasGTMConsent } from '@/lib/gtm'
import { Button } from './button'
import { Card } from './card'

export default function CookieConsent() {
  const { t } = useLocale()
  const [isVisible, setIsVisible] = useState(false)
  const [isLoaded, setIsLoaded] = useState(false)

  useEffect(() => {
    // Wait for client-side hydration
    setIsLoaded(true)
    
    // Early exit if we're not on the main application
    if (!isMainApplication()) return
    
    if (shouldShowConsentBanner()) {
      setIsVisible(true)
    } else if (hasGTMConsent() === true) {
      // Only initialize GTM if user previously accepted
      initializeGTM()
    }
    // If user previously declined, don't load GTM at all
  }, [])

  const handleAccept = () => {
    initializeGTM()
    setGTMConsent(true)
    setIsVisible(false)
  }

  const handleDecline = () => {
    setGTMConsent(false)
    setIsVisible(false)
  }

  // Don't render until client-side loaded
  if (!isLoaded || !isVisible) {
    return null
  }

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-3 sm:p-4">
      <Card className="mx-auto max-w-4xl border border-indigo-200/20 bg-gradient-to-r from-slate-900 via-indigo-950 to-purple-950 text-white shadow-2xl backdrop-blur-sm">
        <div className="p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row items-start gap-4">
            {/* Cookie icon with gradient background */}
            <div className="flex-shrink-0 sm:mt-1">
              <div className="w-8 h-8 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center">
                <svg 
                  width="18" 
                  height="18" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  xmlns="http://www.w3.org/2000/svg"
                  className="text-white"
                >
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                  <circle cx="8" cy="10" r="1" fill="currentColor"/>
                  <circle cx="16" cy="10" r="1" fill="currentColor"/>
                  <circle cx="10" cy="15" r="1" fill="currentColor"/>
                  <circle cx="14" cy="15" r="1" fill="currentColor"/>
                  <circle cx="12" cy="8" r="1" fill="currentColor"/>
                </svg>
              </div>
            </div>
            
            <div className="flex-1 space-y-3">
              <h3 className="text-sm sm:text-base font-medium leading-relaxed text-white">
                {t('cookie_consent.title')}
              </h3>
              <p className="text-xs sm:text-sm text-indigo-100/80 leading-relaxed">
                {t('cookie_consent.description')}
              </p>
            </div>

            <div className="flex flex-col sm:flex-row flex-shrink-0 gap-3 w-full sm:w-auto sm:ml-4">
              <Button
                onClick={handleDecline}
                variant="outline"
                size="sm"
                className="border-indigo-400/30 bg-transparent text-indigo-100 hover:bg-indigo-900/50 hover:text-white hover:border-indigo-300/50 transition-all duration-200 w-full sm:w-auto"
              >
                {t('cookie_consent.decline')}
              </Button>
              <Button
                onClick={handleAccept}
                size="sm"
                className="bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white border-0 shadow-lg hover:shadow-xl transition-all duration-200 w-full sm:w-auto"
              >
                {t('cookie_consent.accept_all')}
              </Button>
            </div>
          </div>
        </div>
      </Card>
    </div>
  )
}